<template>
	<div class="pageRoot">
		<div v-if="(error)" class="error">{{error}}</div>
		<div v-else-if="loading" class="loading"><ScaleLoader /> Loading…</div>
		<div v-else-if="editObj && editSpec" class="">
			<div class="awsLookupRow">
				<input type="button" value="Look Up AWS Resources" @click="openAwsPanel" />
				<span class="awsLookupHint">Lists the Target Group ARNs and EC2 instance IDs available in the configured AWS account, for copying into the fields below.</span>
			</div>
			<Editor :object="editObj" :spec="editSpec" @valueChanged="valueChanged" />
			<div class="buttonRow">
				<input type="button" value="Save Changes" @click="SaveChanges" /> <input type="button" value="Forget Changes / Reload Form" @click="ForgetChanges" />
			</div>
			<div class="buttonRow">
				<input type="button" value="Restart Server" @click="RestartServerClicked" />
			</div>
		</div>

		<!-- Non-modal panel pinned to the top right of the viewport so it can remain open while editing the settings inputs. -->
		<div v-if="awsPanelVisible" class="awsResourcePanel" role="dialog" aria-label="AWS Resources">
			<div class="awsPanelHeader">
				<span class="awsPanelTitle">AWS Resources</span>
				<input type="button" value="Refresh" @click="loadAwsResources" :disabled="awsPanelLoading" />
				<button type="button" class="awsPanelClose" @click="awsPanelVisible = false" title="Close">✕</button>
			</div>
			<div v-if="awsPanelLoading" class="awsPanelLoading"><ScaleLoader /> Querying AWS…</div>
			<div v-else-if="awsPanelError" class="error">{{awsPanelError}}</div>
			<template v-else>
				<div class="awsPanelSection">Target Groups</div>
				<p v-if="!awsTargetGroups.length" class="awsPanelEmpty">No target groups were found.</p>
				<div v-for="tg in awsTargetGroups" :key="tg.arn" class="awsResourceRow">
					<div class="awsResourceName">{{tg.name}} <span class="awsResourceMeta">{{tg.protocol}}:{{tg.port}}</span></div>
					<div class="awsResourceValueRow">
						<code class="awsResourceValue">{{tg.arn}}</code>
						<button type="button" class="awsCopyBtn" @click="copyResourceValue(tg.arn)" :title="'Copy the ARN of \'' + tg.name + '\' to the clipboard'">Copy</button>
					</div>
				</div>
				<div class="awsPanelSection">EC2 Instances</div>
				<p v-if="!awsInstances.length" class="awsPanelEmpty">No instances were found.</p>
				<div v-for="inst in awsInstances" :key="inst.instanceId" class="awsResourceRow">
					<div class="awsResourceName">{{inst.name || '(unnamed)'}} <span class="awsResourceMeta">{{inst.state}}</span></div>
					<div class="awsResourceValueRow">
						<code class="awsResourceValue">{{inst.instanceId}}</code>
						<button type="button" class="awsCopyBtn" @click="copyResourceValue(inst.instanceId)" :title="'Copy the instance ID of \'' + (inst.name || inst.instanceId) + '\' to the clipboard'">Copy</button>
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script>
	import Editor from 'appRoot/vues/common/editor/Editor.vue';
	import { GetSettingsData, SetSettingsData, RestartServer } from 'appRoot/api/SettingsData';
	import { GetAwsTargetGroupResources } from 'appRoot/api/MonitoringConfig';
	import { ModalConfirmDialog } from 'appRoot/scripts/ModalDialog';
	import { CopyToClipboard } from 'appRoot/scripts/Util';

	export default {
		components: { Editor },
		data()
		{
			return {
				error: null,
				loading: false,
				editObj: null,
				editSpec: null,
				awsPanelVisible: false,
				awsPanelLoading: false,
				awsPanelError: null,
				awsTargetGroups: [],
				awsInstances: []
			};
		},
		computed:
		{
		},
		methods:
		{
			loadSettings()
			{
				this.loading = true;
				this.editObj = null;
				this.editSpec = null;
				GetSettingsData()
					.then(data =>
					{
						if (data.success)
						{
							this.editObj = data.settings;
							this.editSpec = data.editSpec;

							if (data.settings.loginStyle)
								appContext.loginStyle = data.settings.loginStyle;
						}
						else
							this.error = data.error;
					})
					.catch(err =>
					{
						this.error = err.message;
					})
					.finally(() =>
					{
						this.loading = false;
					});
			},
			valueChanged(key, value)
			{
				this.editObj[key] = value;
			},
			SaveChanges()
			{
				this.loading = true;
				this.editObj.appPath = '/' + this.editObj.appPath.replace(/^\/+|\/+$/g, '') + '/';
				if (this.editObj.appPath === '//')
					this.editObj.appPath = '/';
				SetSettingsData(this.editObj)
					.then(data =>
					{
						if (data.success)
						{
							if (data.message)
								toaster.success(data.message);
							appContext.appPath = this.editObj.appPath;
							this.loadSettings();
						}
						else
						{
							toaster.error(data.error);
							this.loading = false;
						}
					})
					.catch(err =>
					{
						toaster.error(err.message);
						this.loading = false;
					});
			},
			ForgetChanges()
			{
				this.loadSettings();
			},
			openAwsPanel()
			{
				this.awsPanelVisible = true;
				this.loadAwsResources();
			},
			loadAwsResources()
			{
				this.awsPanelLoading = true;
				this.awsPanelError = null;
				GetAwsTargetGroupResources()
					.then(data =>
					{
						if (data.success)
						{
							this.awsTargetGroups = data.targetGroups || [];
							this.awsInstances = data.instances || [];
						}
						else
							this.awsPanelError = data.error;
					})
					.catch(err =>
					{
						this.awsPanelError = err.message || String(err);
					})
					.finally(() =>
					{
						this.awsPanelLoading = false;
					});
			},
			copyResourceValue(value)
			{
				CopyToClipboard(value);
				toaster.Success('Copied "' + value + '" to the clipboard.');
			},
			RestartServerClicked()
			{
				ModalConfirmDialog("Are you sure you want to restart the service? Active web interface sessions will be lost.", "Confirm")
					.then(userClickedYes =>
					{
						if (userClickedYes)
						{
							this.loading = true;
							RestartServer()
								.then(data =>
								{
									setTimeout(() =>
									{
										this.loadSettings();
									}, 4000);
								})
								.catch(err =>
								{
									this.loading = false;
									toaster.error(err);
								});
						}
					});
			}
		},
		created()
		{
			this.loadSettings();
		}
	}
</script>

<style scoped>
	.pageRoot
	{
		margin: 8px;
	}

	.loading
	{
		margin-top: 80px;
		text-align: center;
	}

	.error
	{
		color: #FF0000;
		font-weight: bold;
	}

	.buttonRow
	{
		margin-top: 30px;
		border-top: 1px solid #AAAAAA;
		padding-top: 10px;
	}

	.awsLookupRow
	{
		margin-bottom: 14px;
	}

	.awsLookupHint
	{
		margin-left: 10px;
		color: #666666;
		font-size: 0.9em;
	}

	.awsResourcePanel
	{
		position: fixed;
		top: 10px;
		right: 10px;
		width: 470px;
		max-width: calc(100vw - 20px);
		max-height: calc(100vh - 20px);
		overflow-y: auto;
		background-color: #FFFFFF;
		border: 1px solid #999999;
		border-radius: 6px;
		box-shadow: 0 4px 24px rgba(0,0,0,0.35);
		padding: 12px;
		box-sizing: border-box;
		z-index: 100;
	}

	.awsPanelHeader
	{
		display: flex;
		align-items: center;
		gap: 10px;
		margin-bottom: 8px;
	}

	.awsPanelTitle
	{
		font-size: 1.2em;
		font-weight: bold;
		flex: 1 1 auto;
	}

	.awsPanelClose
	{
		flex: 0 0 auto;
		background: none;
		border: 1px solid #999999;
		border-radius: 4px;
		padding: 2px 8px;
		cursor: pointer;
		font-size: 14px;
	}

		.awsPanelClose:hover
		{
			background-color: #EEEEEE;
		}

	.awsPanelLoading
	{
		display: flex;
		align-items: center;
		gap: 10px;
		padding: 8px 0;
	}

	.awsPanelSection
	{
		font-weight: bold;
		color: #555555;
		text-transform: uppercase;
		font-size: 0.8em;
		letter-spacing: 0.05em;
		border-bottom: 2px solid #DDDDDD;
		padding-bottom: 3px;
		margin: 14px 0 6px 0;
	}

		.awsPanelSection:first-of-type
		{
			margin-top: 4px;
		}

	.awsPanelEmpty
	{
		color: #666666;
		font-style: italic;
		margin: 6px 0;
	}

	.awsResourceRow
	{
		padding: 6px 0;
		border-bottom: 1px solid #EEEEEE;
	}

		.awsResourceRow:last-child
		{
			border-bottom: none;
		}

	.awsResourceName
	{
		font-weight: bold;
	}

	.awsResourceMeta
	{
		font-weight: normal;
		color: #888888;
		font-size: 0.85em;
		margin-left: 4px;
	}

	.awsResourceValueRow
	{
		display: flex;
		align-items: flex-start;
		gap: 8px;
		margin-top: 2px;
	}

	.awsResourceValue
	{
		min-width: 0;
		font-family: Consolas, monospace;
		font-size: 0.85em;
		background-color: #F4F4F4;
		border-radius: 3px;
		padding: 2px 6px;
		word-break: break-all;
	}

	.awsCopyBtn
	{
		flex: 0 0 auto;
		font-size: 0.8em;
		padding: 2px 8px;
		border: 1px solid #999999;
		border-radius: 3px;
		background-color: #FAFAFA;
		cursor: pointer;
	}

		.awsCopyBtn:hover
		{
			background-color: #FFFFFF;
			border-color: #555555;
		}
</style>